import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, Injectable, Type } from "@angular/core";
import { FloatuiDisplayComponent } from "../components/floatui-display.component";
import { autoUpdate, computePosition, flip, offset, Placement, shift } from "@floating-ui/dom";
import { EventData, FloatuiElement } from "../model/floatui-element.model";
import { Observable } from "rxjs";

interface ComponentData {
    wrapper: HTMLElement;
    ref: ComponentRef<unknown>;
    cleanup: () => void;
}

interface AddComponentOptions {
    placement: Placement;
    inputs?: Record<string, unknown>;
    eventSubscriber?: (observable: Observable<EventData>) => void;
}


@Injectable()
export class FloatUIService {

    private component!: FloatuiDisplayComponent;
    private components: Record<string, ComponentData> = {};

    constructor(
        private environmentInjector: EnvironmentInjector,
        private appRef: ApplicationRef,
    ) {}

    public registerRootComponent(component: FloatuiDisplayComponent) {
        this.component = component;
    }

    public addComponent(component: Type<FloatuiElement>, reference: HTMLElement, options: AddComponentOptions ): string {
        const id = crypto.randomUUID();

        const wrapper = document.createElement('div');
        wrapper.classList.add('float-element-wrapper');

        const ref = createComponent(component, {
            environmentInjector: this.environmentInjector,
            hostElement: wrapper,
        });

        if (options.inputs) Object.entries(options.inputs).forEach(([key, value]) => ref.setInput(key, value));
        if (options.eventSubscriber) options.eventSubscriber(ref.instance.output.asObservable());

        this.appRef.attachView(ref.hostView);
        this.component.createComponent(wrapper);

        const cleanup = autoUpdate(
            reference,
            wrapper,
            async () => {
                if (!reference.isConnected) {
                    this.removeComponent(id);
                    return;
                }
                
                const { x, y } = await computePosition(reference, wrapper, {
                    placement: options.placement,
                    middleware: [offset(6), flip(), shift({ padding: 5 })],
                });
                Object.assign(wrapper.style, { left: `${x}px`, top: `${y}px` });
            }
        );

        this.components[id] = { wrapper, ref, cleanup}

        return id
    }

    public removeComponent(id: string): void {
        const cmp = this.components[id];
        if (!cmp) return;

        const { wrapper, ref, cleanup } = cmp;

        try {
            cleanup?.();
        } catch {}

        this.component.destroyComponent(wrapper);
        this.appRef.detachView(ref.hostView);
        ref.destroy();

        delete this.components[id];
    }

}